/**
 * Per-tenant color palette for the public website widgets.
 *
 * The brand's colors are configured in the apex admin (`color_1` / `color_2`, see
 * `TenantsPage.vue`) and reach the SPA through the server-inlined
 * `<script id="branding-bootstrap">` payload, exposed by `loadBranding()` as
 * `color1` / `color2` (hex WITHOUT the leading '#', `Tenant#write_color`,
 * app/models/tenant.rb:233-241).
 *
 * A brand that has not configured colors must render neutral: black & white.
 * That is why the absence of a valid `color1` yields a monochrome palette
 * instead of the historical hardcoded purple.
 *
 * This module is pure (no DOM) so it can be unit-tested and reused by every
 * widget that needs the tenant palette.
 */

export interface BrandPalette {
  /** Main brand color, or `#111111` for the black & white fallback. */
  primary: string;
  /** Darker shade of the primary, used for gradients and pressed states. */
  primaryDark: string;
  /** Very light tint of the primary, used for soft backgrounds. */
  primarySoft: string;
  /** Deep, readable text shade derived from the primary. */
  ink: string;
  /** `r, g, b` of the primary, for `rgba(var(--brand-primary-rgb), a)` usage. */
  rgb: string;
  /** Ready-to-use gradient for active/selected surfaces. */
  gradient: string;
  /** False when the tenant has no valid colors and the palette is monochrome. */
  hasCustomColors: boolean;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX_PATTERN = /^#?([0-9a-fA-F]{6})$/;
const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

const MONOCHROME = {
  primary: '#111111',
  primaryDark: '#000000',
  primarySoft: '#f5f5f5',
  ink: '#111111',
  rgb: '17, 17, 17',
} as const;

const parseHex = (value: string | null | undefined): Rgb | null => {
  if (value == null) return null;
  const match = HEX_PATTERN.exec(String(value).trim());
  if (!match) return null;
  const hex = match[1].toLowerCase();
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

const channel = (n: number): string =>
  Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, '0');

const toHex = ({ r, g, b }: Rgb): string => `#${channel(r)}${channel(g)}${channel(b)}`;

/** Blends `from` toward `to`; `weight` is the amount of `to` (0..1). */
const mix = (from: Rgb, to: Rgb, weight: number): Rgb => ({
  r: from.r * (1 - weight) + to.r * weight,
  g: from.g * (1 - weight) + to.g * weight,
  b: from.b * (1 - weight) + to.b * weight,
});

/**
 * Builds the palette for a tenant.
 *
 * @param color1 Primary color from the branding bootstrap (`color1`).
 * @param color2 Secondary color, optionally used to enrich the gradient.
 */
export function brandPalette(
  color1: string | null | undefined,
  color2: string | null | undefined,
): BrandPalette {
  const primary = parseHex(color1);

  if (!primary) {
    return {
      ...MONOCHROME,
      gradient: `linear-gradient(135deg, ${MONOCHROME.primary}, ${MONOCHROME.primaryDark})`,
      hasCustomColors: false,
    };
  }

  const secondary = parseHex(color2) ?? mix(primary, BLACK, 0.25);

  return {
    primary: toHex(primary),
    primaryDark: toHex(mix(primary, BLACK, 0.25)),
    primarySoft: toHex(mix(primary, WHITE, 0.9)),
    ink: toHex(mix(primary, BLACK, 0.72)),
    rgb: `${Math.round(primary.r)}, ${Math.round(primary.g)}, ${Math.round(primary.b)}`,
    gradient: `linear-gradient(135deg, ${toHex(primary)}, ${toHex(secondary)})`,
    hasCustomColors: true,
  };
}

/** Formats a palette as CSS custom properties to bind on a component root. */
export function brandCssVars(palette: BrandPalette): Record<string, string> {
  return {
    '--brand-primary': palette.primary,
    '--brand-primary-dark': palette.primaryDark,
    '--brand-primary-soft': palette.primarySoft,
    '--brand-primary-rgb': palette.rgb,
    '--brand-ink': palette.ink,
    '--brand-gradient': palette.gradient,
    '--brand-on-primary': '#ffffff',
  };
}
